import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  Add as AddTimeIcon
} from '@mui/icons-material';
import { locationService } from '../../../services/locationService';
import { locationAvailabilityService } from '../../../services/locationAvailabilityService';

const LocationsAdmin = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    isHub: false,
    isActive: true
  });

  // Location availabilities management
  const [availabilitiesDialogOpen, setAvailabilitiesDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationAvailabilities, setLocationAvailabilities] = useState([]);
  const [availabilitiesLoading, setAvailabilitiesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // New availability form
  const [newAvailability, setNewAvailability] = useState({
    dayOfWeek: 'MONDAY',
    startTime: '09:00',
    endTime: '10:00'
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await locationService.getAllActiveLocations();
      setLocations(data);
    } catch (err) {
      setError('Failed to load locations');
      console.error('Error loading locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name,
        isHub: location.isHub,
        isActive: location.isActive
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        isHub: false,
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
    setFormData({
      name: '',
      isHub: false,
      isActive: true
    });
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');
      
      if (!formData.name.trim()) {
        setError('Location name is required');
        return;
      }

      if (editingLocation) {
        // Update existing location
        await locationService.updateLocation(editingLocation.id, formData);
        setSuccess('Location updated successfully');
      } else {
        // Create new location
        await locationService.createLocation(formData);
        setSuccess('Location created successfully');
      }
      
      handleCloseDialog();
      loadLocations();
    } catch (err) {
      setError(editingLocation ? 'Failed to update location' : 'Failed to create location');
      console.error('Error saving location:', err);
    }
  };

  const handleDelete = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      try {
        await locationService.deleteLocation(locationId);
        setSuccess('Location deleted successfully');
        loadLocations();
      } catch (err) {
        setError('Failed to delete location');
        console.error('Error deleting location:', err);
      }
    }
  };

  // Location availabilities management
  const handleOpenAvailabilitiesDialog = async (location) => {
    setSelectedLocation(location);
    setAvailabilitiesDialogOpen(true);
    setAvailabilitiesLoading(true);
    
    try {
      const availabilities = await locationAvailabilityService.getAvailabilitiesByLocationId(location.id);
      setLocationAvailabilities(availabilities);
    } catch (err) {
      setError('Failed to load location availabilities');
      console.error('Error loading availabilities:', err);
    } finally {
      setAvailabilitiesLoading(false);
    }
  };

  const handleCloseAvailabilitiesDialog = () => {
    setAvailabilitiesDialogOpen(false);
    setSelectedLocation(null);
    setLocationAvailabilities([]);
    setActiveTab(0);
    setNewAvailability({
      dayOfWeek: 'MONDAY',
      startTime: '09:00',
      endTime: '10:00'
    });
  };

  const handleAddAvailability = async () => {
    try {
      if (!newAvailability.startTime || !newAvailability.endTime) {
        setError('Start time and end time are required');
        return;
      }

      if (newAvailability.startTime >= newAvailability.endTime) {
        setError('End time must be after start time');
        return;
      }

      const availabilityData = {
        locationId: selectedLocation.id,
        dayOfWeek: newAvailability.dayOfWeek,
        startTime: newAvailability.startTime,
        endTime: newAvailability.endTime,
        isActive: true
      };

      await locationAvailabilityService.createLocationAvailability(availabilityData);
      
      // Reload availabilities
      const availabilities = await locationAvailabilityService.getAvailabilitiesByLocationId(selectedLocation.id);
      setLocationAvailabilities(availabilities);
      
      // Reset form
      setNewAvailability({
        dayOfWeek: 'MONDAY',
        startTime: '09:00',
        endTime: '10:00'
      });
      
      setSuccess('Time slot added successfully');
    } catch (err) {
      setError('Failed to add time slot');
      console.error('Error adding availability:', err);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      try {
        await locationAvailabilityService.deleteLocationAvailability(availabilityId);
        
        // Reload availabilities
        const availabilities = await locationAvailabilityService.getAvailabilitiesByLocationId(selectedLocation.id);
        setLocationAvailabilities(availabilities);
        
        setSuccess('Time slot deleted successfully');
      } catch (err) {
        setError('Failed to delete time slot');
        console.error('Error deleting availability:', err);
      }
    }
  };

  const formatTime = (time) => {
    // Parse the time string (expected format: "HH:mm" or "HH:mm:ss")
    const [hours, minutes] = time.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return time; // Return original if parsing fails
    }
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    // Format minutes with leading zero if needed
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  const formatDay = (day) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Locations Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Location
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.map((location) => (
                <TableRow key={location.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {location.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {location.isHub ? (
                      <Chip label="Hub Location" color="primary" size="small" />
                    ) : (
                      <Chip label="Satellite" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={location.isActive ? 'Active' : 'Inactive'}
                      color={location.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="secondary"
                      onClick={() => handleOpenAvailabilitiesDialog(location)}
                      size="small"
                      title="Manage Time Slots"
                    >
                      <ScheduleIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(location)}
                      size="small"
                      title="Edit Location"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(location.id)}
                      size="small"
                      title="Delete Location"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Location Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLocation ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Location Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              margin="normal"
              required
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isHub}
                  onChange={(e) => handleInputChange('isHub', e.target.checked)}
                />
              }
              label="Hub Location (e.g., GATE Magnet School)"
              sx={{ mt: 2 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingLocation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Availabilities Management Dialog */}
      <Dialog 
        open={availabilitiesDialogOpen} 
        onClose={handleCloseAvailabilitiesDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              Manage Time Slots - {selectedLocation?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
              <Tab label="Current Time Slots" />
              <Tab label="Add New Time Slot" />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                {availabilitiesLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : locationAvailabilities.length === 0 ? (
                  <Alert severity="info">
                    No time slots configured for this location. Add some time slots to get started.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'].map((day) => {
                      const dayAvails = locationAvailabilities.filter(avail => avail.dayOfWeek === day);
                      if (dayAvails.length === 0) return null;

                      return (
                        <Grid item xs={12} md={6} key={day}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                                {formatDay(day)}
                              </Typography>
                              {dayAvails.map((availability) => (
                                <Box 
                                  key={availability.id} 
                                  display="flex" 
                                  alignItems="center" 
                                  justifyContent="space-between"
                                  sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}
                                >
                                  <Typography variant="body2">
                                    {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                                  </Typography>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleDeleteAvailability(availability.id)}
                                    title="Delete time slot"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Add New Time Slot
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Day of Week</InputLabel>
                      <Select
                        value={newAvailability.dayOfWeek}
                        onChange={(e) => setNewAvailability(prev => ({ ...prev, dayOfWeek: e.target.value }))}
                        label="Day of Week"
                      >
                        <MenuItem value="MONDAY">Monday</MenuItem>
                        <MenuItem value="TUESDAY">Tuesday</MenuItem>
                        <MenuItem value="WEDNESDAY">Wednesday</MenuItem>
                        <MenuItem value="THURSDAY">Thursday</MenuItem>
                        <MenuItem value="FRIDAY">Friday</MenuItem>
                        <MenuItem value="SATURDAY">Saturday</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={newAvailability.startTime}
                      onChange={(e) => setNewAvailability(prev => ({ ...prev, startTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={newAvailability.endTime}
                      onChange={(e) => setNewAvailability(prev => ({ ...prev, endTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddTimeIcon />}
                    onClick={handleAddAvailability}
                  >
                    Add Time Slot
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvailabilitiesDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsAdmin;
